import { useCallback, useEffect, useMemo } from "react";

import { useAtom, useAtomValue } from "jotai";

import { useTranslation } from "react-i18next";

import i18n from "i18next";

import {
  BroadcastStartError,
  OSR_START_FAILED,
  OSR_START_TIMEOUT,
} from "../../broadcast/controlBroadcast";

import { useLiveQuery } from "dexie-react-hooks";

import { openDatabase } from "../../Utils/db";

import { getScopedStreamOutputs } from "../../Utils/streamOutputScope";

import Radio from "../../entities/Radio";

import CurrentUser from "../../entities/User";

import CurrentWorkspace from "../../entities/Workspace";

import { UserType } from "../../interfaces/User";

import { useAudioConfirmation } from "../../hooks/useAudioConfirmation";

import { useEncoderValidation } from "../../hooks/useEncoderValidation";

import { subscribeToTransmissionEvents } from "../../Utils/navigationEvents";

import {
  atomAuth,
  atomBroadcastDisabled,
  atomHaveOutputStream,
  atomLoadingAccount,
  atomOnLive,
  atomBroadcastLoading,
  atomRecordingLoading,
} from "./CustomTitleBar.atoms";

import { authBootstrapStateAtom } from "../../atoms/auth.atoms";

import {
  atomStreamConfigurations,
  type StreamConfigurations,
} from "../../atoms/atomsStream";

import { atomShowAssembler } from "../../screens/Dashboard/SubComponents/Studio/Studio.atoms";

import {
  toastSuccess,
  toastError,
  toastWarning,
} from "../../services/toastService";

import { maybeShowConcurrentLimitToast } from "../../services/concurrentLimitToast";

import { SceneSyncChannel } from "../../broadcast/sceneSync";

export const useBroadcast = () => {
  const [disabled, setDisabled] = useAtom(atomBroadcastDisabled);

  const haveOutputStream = useAtomValue(atomHaveOutputStream);

  const [, setStreamConfigs] = useAtom(atomStreamConfigurations);

  const [onLive, setOnLive] = useAtom(atomOnLive);

  const [isLoading, setIsLoading] = useAtom(atomBroadcastLoading);

  const [showAssembler] = useAtom(atomShowAssembler);

  const {
    showModal,

    availableMicrophones,

    handleContinueWithoutAudio,

    handleCancel: handleAudioCancel,
  } = useAudioConfirmation();

  const { validateBeforeBroadcast } = useEncoderValidation();

  // Sync stream configurations from IndexedDB into the atom on startup

  // and whenever the DB changes.  The derived `atomHaveOutputStream`

  // reacts automatically.

  useLiveQuery(async () => {
    const db = await openDatabase();

    const outputs = await getScopedStreamOutputs(db);

    const updated: StreamConfigurations = {};

    for (const output of outputs) {
      const key = output.name;

      updated[key] = output.stream;
    }

    // Only overwrite keys that exist in the DB so that we don't clobber

    // the default atom values for services the user has never saved.

    setStreamConfigs((prev) => {
      // Fast shallow-equal check to avoid unnecessary re-renders

      let changed = false;

      const next = { ...prev };

      for (const [k, v] of Object.entries(updated)) {
        if (
          prev[k]?.enabled !== v.enabled ||
          prev[k]?.streamUrl !== v.streamUrl ||
          prev[k]?.streamKey !== v.streamKey
        ) {
          changed = true;

          next[k] = v;
        }
      }

      return changed ? next : prev;
    });

    // Return a value so dexie-react-hooks can detect changes and keep the

    // subscription alive (returning undefined can cause stale subscriptions).

    return outputs.length;
  });

  // Update disabled state based on output stream and assembler

  useEffect(() => {
    setDisabled(!haveOutputStream || showAssembler);
  }, [haveOutputStream, showAssembler, setDisabled]);

  // El broadcast real corre en la ventana OSR; sus errores de ARRANQUE (ej. cupo de

  // workspaces concurrentes cloud) no llegan al catch de handleStart (la delegación al

  // OSR es fire-and-forget). El OSR los relaya por el canal → acá mostramos el toast.

  // INVARIANTE: BroadcastChannel NO bufferea; este listener debe estar suscripto ANTES

  // de cualquier start. Se cumple porque CustomTitleBar monta al arrancar la app, mucho

  // antes de que el usuario pueda transmitir. Si esto se montara lazy, el error se perdería.

  useEffect(() => {
    const channel = new SceneSyncChannel();

    const off = channel.on((msg) => {
      // Aviso NO-fatal: el broadcast local sigue al aire (ej. la parte cloud se

      // degradó por falta de plan). NO tocamos onLive/isLoading — solo informamos.

      if (msg.type === "broadcast-warning") {
        const { code } = msg;

        void (async () => {
          try {
            await i18n.loadNamespaces("dashboard");

            toastWarning(
              i18n.t(`dashboard:DASHBOARD.broadcastWarnings.${code}`),
              { autoClose: 8000 },
            );
          } catch {
            // Si i18n/namespace falla, no perdemos el aviso: fallback legible.

            toastWarning(String(code), { autoClose: 8000 });
          }
        })();

        return;
      }

      if (msg.type !== "broadcast-error") return;

      setOnLive(false);

      setIsLoading(false);

      void (async () => {
        const handled = await maybeShowConcurrentLimitToast(msg.raw);

        if (!handled) {
          toastError(msg.raw, { autoClose: 8000 });
        }
      })();
    });

    return () => {
      off();

      channel.close();
    };
  }, [setOnLive, setIsLoading]);

  // Listen to streaming events to sync onLive state

  useEffect(() => {
    const radio = new Radio();

    const handleStarted = () => {
      setOnLive(true);
    };

    const handleStopped = () => {
      setOnLive(false);
    };

    const handleError = () => {
      // On any streaming error, reset onLive state

      setOnLive(false);
    };

    const handleRtmpError = (data: unknown) => {
      const shouldStop =
        (data as { shouldStop?: boolean } | null)?.shouldStop ?? false;

      if (shouldStop) {
        setOnLive(false);
      }
    };

    // Subscribe to streaming events

    // Note: connection-lost is non-terminal (stream may reconnect), so we don't reset onLive for it

    radio.Streaming.on("started", handleStarted);

    radio.Streaming.on("stopped", handleStopped);

    radio.Streaming.on("error", handleError);

    radio.Streaming.on("server-error", handleError);

    radio.Streaming.on("rtmp-error", handleRtmpError);

    // Check initial state

    if (radio.Streaming.isStreaming()) {
      setOnLive(true);
    }

    return () => {
      radio.Streaming.off("started", handleStarted);

      radio.Streaming.off("stopped", handleStopped);

      radio.Streaming.off("error", handleError);

      radio.Streaming.off("server-error", handleError);

      radio.Streaming.off("rtmp-error", handleRtmpError);
    };
  }, [setOnLive]);

  const handleStart = useCallback(async () => {
    // Guard de re-entrancy: el botón visible se deshabilita en loading, pero los paths

    // de shortcut/menú llaman handleStart sin chequearlo → evita doble start (doble POST

    // cloud / doble toast de límite).

    if (isLoading) return;

    const encodersValid = await validateBeforeBroadcast();

    if (!encodersValid) {
      console.log("Streaming cancelled: encoder configuration invalid");

      return;
    }

    setIsLoading(true);

    const radio = new Radio();

    try {
      await radio.start();

      // El broadcast corre en la ventana OSR (Radio aparte); su evento "started" NO llega a

      // este Radio del control → reflejamos onLive al iniciar OK (sino el botón queda en

      // "Transmitir" aunque estemos en vivo).

      setOnLive(true);
    } catch (error) {
      console.error("Error starting broadcast:", error);

      const code = error instanceof BroadcastStartError ? error.code : undefined;

      // OSR_START_FAILED: el OSR ya reportó el fallo por el canal (toast del listener de arriba).
      if (code !== OSR_START_FAILED) {
        const errorMsg =
          code === OSR_START_TIMEOUT
            ? i18n.t("dashboard:DASHBOARD.broadcastStartTimeout")
            : error instanceof Error
              ? error.message
              : String(error);

        toastError(errorMsg, { autoClose: 8000 });
      }

      setOnLive(false);
    } finally {
      setIsLoading(false);
    }
  }, [validateBeforeBroadcast, setIsLoading, setOnLive, isLoading]);

  const handleStop = useCallback(() => {
    const radio = new Radio();

    radio.stop();

    // El "stopped" del Radio del OSR tampoco llega acá → reflejamos al detener.

    setOnLive(false);
  }, [setOnLive]);

  // Listen for transmission start events from MenuBar

  useEffect(() => {
    const unsubscribe = subscribeToTransmissionEvents(() => {
      if (!disabled && !onLive) {
        handleStart();
      }
    });

    return unsubscribe;
  }, [disabled, onLive, handleStart]);

  // Listen for keyboard shortcut sceneControls.playPause

  useEffect(() => {
    const electronAPI = window.electronAPI;

    if (!electronAPI || !electronAPI.onShortcut) return;

    const handleShortcut = (event: {
      detail: {
        key: string;

        category?: string;

        action?: string;

        combo?: string;

        timestamp: number;
      };
    }) => {
      const { category, action } = event.detail;

      if (category === "streaming" && action === "startStop") {
        if (!disabled && !onLive) {
          handleStart();
        } else if (onLive) {
          handleStop();
        }
      }
    };

    electronAPI.onShortcut(handleShortcut);

    return () => {
      electronAPI?.removeShortcutListeners?.(handleShortcut);
    };
  }, [disabled, onLive, handleStart, handleStop]);

  return {
    disabled,

    onLive,

    isLoading,

    showAssembler,

    haveOutputStream,

    handleStart,

    handleStop,

    showModal,

    availableMicrophones,

    handleContinueWithoutAudio,

    handleAudioCancel,
  };
};

export const useRecording = () => {
  const { t } = useTranslation("streaming");

  const [disabled] = useAtom(atomBroadcastDisabled);

  const [onLive, setOnLive] = useAtom(atomOnLive);

  const [isLoading, setIsLoading] = useAtom(atomRecordingLoading);

  const [showAssembler] = useAtom(atomShowAssembler);

  const {
    showModal,

    availableMicrophones,

    handleContinueWithoutAudio,

    handleCancel: handleAudioCancel,
  } = useAudioConfirmation();

  const { validateBeforeBroadcast } = useEncoderValidation();

  // Listen to streaming events to sync onLive state

  useEffect(() => {
    const radio = new Radio();

    const handleStarted = () => {
      setOnLive(true);
    };

    const handleStopped = () => {
      setOnLive(false);
    };

    const handleError = () => {
      // On any streaming error, reset onLive state

      setOnLive(false);
    };

    const handleRtmpError = (data: unknown) => {
      const shouldStop =
        (data as { shouldStop?: boolean } | null)?.shouldStop ?? false;

      if (shouldStop) {
        setOnLive(false);
      }
    };

    // Subscribe to streaming events

    // Note: connection-lost is non-terminal (stream may reconnect), so we don't reset onLive for it

    radio.Streaming.on("started", handleStarted);

    radio.Streaming.on("stopped", handleStopped);

    radio.Streaming.on("error", handleError);

    radio.Streaming.on("server-error", handleError);

    radio.Streaming.on("rtmp-error", handleRtmpError);

    // Check initial state

    if (radio.Streaming.isStreaming()) {
      setOnLive(true);
    }

    return () => {
      radio.Streaming.off("started", handleStarted);

      radio.Streaming.off("stopped", handleStopped);

      radio.Streaming.off("error", handleError);

      radio.Streaming.off("server-error", handleError);

      radio.Streaming.off("rtmp-error", handleRtmpError);
    };
  }, [setOnLive]);

  const handleStart = useCallback(async () => {
    const encodersValid = await validateBeforeBroadcast();

    if (!encodersValid) {
      console.log("Recording cancelled: encoder configuration invalid");

      return;
    }

    setIsLoading(true);

    const radio = new Radio();

    try {
      await radio.start();
    } catch (error) {
      console.error("Error starting recording:", error);

      const errorMsg = error instanceof Error ? error.message : String(error);

      toastError(errorMsg, { autoClose: 8000 });
    } finally {
      setIsLoading(false);
    }

    // Don't set onLive here - wait for the "started" event
  }, [validateBeforeBroadcast, setIsLoading]);

  const handleStop = useCallback(() => {
    const radio = new Radio();

    radio.stop();

    // Don't set onLive here - wait for the "stopped" event

    toastSuccess(t("RECORDING_NOTIFICATIONS.stoppedAndSaved"));
  }, [t]);

  useEffect(() => {
    const unsubscribe = subscribeToTransmissionEvents(() => {
      if (!onLive) {
        handleStart();
      }
    });

    return unsubscribe;
  }, [onLive, handleStart]);

  // Listen for keyboard shortcut streaming.startStop

  useEffect(() => {
    const electronAPI = window.electronAPI;

    if (!electronAPI || !electronAPI.onShortcut) return;

    const handleShortcut = (event: {
      detail: {
        key: string;

        category?: string;

        action?: string;

        combo?: string;

        timestamp: number;
      };
    }) => {
      const { category, action } = event.detail;

      if (category === "streaming" && action === "startStop") {
        if (!onLive) {
          handleStart();
        } else if (onLive) {
          handleStop();
        }
      }
    };

    electronAPI.onShortcut(handleShortcut);

    return () => {
      electronAPI?.removeShortcutListeners?.(handleShortcut);
    };
  }, [onLive, handleStart, handleStop]);

  return {
    disabled,

    onLive,

    isLoading,

    showAssembler,

    handleStart,

    handleStop,

    showModal,

    availableMicrophones,

    handleContinueWithoutAudio,

    handleAudioCancel,
  };
};

export const useAuthConnection = () => {
  const [loading, setLoading] = useAtom(atomLoadingAccount);

  const [activeAccount, setActiveAccount] = useAtom(atomAuth);

  // La sesión puede venir por cookie (bootstrap /auth/me) SIN que exista
  // @Eriol/system/userId en localStorage (solo lo escriben Login y deep-link).
  // Si isAuthenticated mira solo localStorage, el menú "Ir" desaparece para
  // sesiones restauradas por cookie. Fuente de verdad = bootstrap, con el
  // localStorage como OR para el path legacy de Electron.
  const bootState = useAtomValue(authBootstrapStateAtom);

  const isAuthenticated = useMemo(
    () => bootState === "authenticated" || activeAccount !== null,

    [bootState, activeAccount],
  );

  useEffect(() => {
    const userId = CurrentUser.getUserId();

    setActiveAccount(userId);

    setLoading(false);
  }, [setActiveAccount, setLoading]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "@Eriol/system/userId") {
        setActiveAccount(e.newValue);
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => window.removeEventListener("storage", handleStorageChange);
  }, [setActiveAccount]);

  useEffect(() => {
    const handleAuthChange = () => {
      const userId = CurrentUser.getUserId();

      setActiveAccount(userId);
    };

    window.addEventListener("auth-changed", handleAuthChange);

    return () => window.removeEventListener("auth-changed", handleAuthChange);
  }, [setActiveAccount]);

  return { activeAccount, isAuthenticated, loading };
};

export const useUserType = () => {
  const userType = useLiveQuery(async () => {
    return await CurrentWorkspace.getType();
  });

  const isLocalRecording = useMemo(
    () => userType === UserType.LocalRecording,

    [userType],
  );

  return { userType, isLocalRecording };
};
